/**
 * 「给这个字造词」的**纯逻辑**（2026-10-30 用户要求）。
 *
 * ## 用户原话
 *
 * > "这个造词功能有点不方便，每次只能造一个词，可不可以一次造多个词？
 * >  然后还要选这词是词语还是成语，你要自动识别造的词是词语还是成语。
 * >  另外，造的词好像也不能修改？万一有错别字怎么办？"
 *
 * 这里放两件可以用单测钉住的事：
 *   1. **一次多词的输入怎么切**（换行 / 空格 / 逗号 / 顿号 / 分号都能分）；
 *   2. **自动判断「词语」还是「成语」**（不让人再手动选类别）。
 *
 * 判断规则（与界面上那三个类别 `common` / `phrase` / `idiom` 对齐）：
 *   · **4 个字及以上 → 成语**（`idiom`）；
 *   · **2～3 个字 → 词语**（`phrase`）；
 *   · 1 个字 → 不合法（"词语表里只能录词语或成语"），交给校验报错。
 */

export type GuessedWordCategory = 'phrase' | 'idiom';

/**
 * 把"一次造多个词"的输入切成一个个词。
 *
 * 分隔符：换行、空格、Tab、`,，、;；/`。**保留**词里的字母/数字（如 `PEP`）。
 * 结果**去重**（同一个词写两遍没有意义），空项丢掉。
 */
export function splitWordBatchInput(raw: string): string[] {
  const parts = raw.split(/[\s,，、;；/]+/u);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of parts) {
    const trimmed = part.trim();
    if (trimmed === '') continue;
    if (seen.has(trimmed)) continue;
    seen.add(trimmed);
    out.push(trimmed);
  }
  return out;
}

/**
 * 自动判断类别：**4 个字及以上算成语，2～3 个字算词语**。
 *
 * 为什么不查成语词典：本项目不联网、词典也没法覆盖"家长自己编的四字词"；
 * 而用户真正在意的是"我不想每次手选" —— 四字以上按成语归类，
 * 错了在编辑里改一下就行（`category` 可以改，`wordText` 只有自造词能改）。
 */
export function guessWordCategory(wordText: string): GuessedWordCategory {
  const chars = [...wordText.trim()];
  return chars.length >= 4 ? 'idiom' : 'phrase';
}

export interface WordBatchItem {
  /** 词面。 */
  text: string;
  /** 自动判断出来的类别（= 会写进 `word.category`）。 */
  category: GuessedWordCategory;
  /** 不合法时的原因（`null` = 这一条可以提交）。 */
  error: string | null;
}

/**
 * 解析一批输入 → 每条带"能不能提交 / 为什么不能"。
 *
 * 只做**形状**校验（至少 2 个汉字、不超过 32 字）：
 * 拼音、是否已存在这类事由服务端判（它会返回可读的中文错误）。
 */
export function parseWordBatch(raw: string): WordBatchItem[] {
  return splitWordBatchInput(raw).map((text) => {
    const chars = [...text];
    const hanziCount = chars.filter((ch) => /\p{Script=Han}/u.test(ch)).length;
    let error: string | null = null;
    if (hanziCount < 2) error = '至少两个汉字（单个字请用「生字」录入）';
    else if (chars.length > 32) error = '太长了（最多 32 个字）';
    return { text, category: guessWordCategory(text), error };
  });
}
